import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from 'framer-motion';
import { ImSpinner2 } from "react-icons/im";
import { Store, useStore } from "../../store";
import { API_URL } from "../../utils/consts";

type SummaryData = {
  meditationTime: number;
  otherUsersCount: number;
  otherUsersTime: number;
  otherUsersCountriesCount: number;
  daysStrike: number;
}

const Summary = () => {
  const { t } = useTranslation()
  const page = useStore((state: Store) => state.page)
  const goToPage = useStore((state: Store) => state.goToPage)
  const userData = useStore((state: Store) => state.userData)

  const [summaryData, setSummaryData] = useState<SummaryData | undefined>();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (userData.id === "null") {
      const userIntention = userData.userIntentions[0];
      setSummaryData({
        meditationTime: ((userIntention.endTime || new Date()).getTime() - userIntention.startTime.getTime()) / 1000,
        otherUsersCount: 0,
        otherUsersTime: 0,
        otherUsersCountriesCount: 0,
        daysStrike: 0,
      })
      setLoading(false)

      return;
    }

    if (page.userIntentionId) {
      axios.get(`/user_intentions/${page.userIntentionId}/summary?user_id=${userData.id}`)
        .then(response => setSummaryData(response.data))
        .catch((err) => console.log(err))
        .finally(() => {
          setLoading(false)
        })
    }
  }, [page.userIntentionId, userData.id]);

  // const meditationTime = moment.duration(summaryData.meditationTime, 'seconds')
  // const summedMeditationTime = moment.duration(summaryData.meditationTime + summaryData.otherUsersTime, 'seconds')
  // const othersMeditationTime = moment.duration(summaryData.otherUsersTime, 'seconds')

  let meditationMinutes = useMemo(() => {
    if (!summaryData) {
      return;
    }

    return Math.floor(summaryData.meditationTime / 60)
  }, [summaryData]);

  return (
    <motion.div
      className="page h-full w-full justify-center items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition:{
          duration: 0.5,
          delay: 0.6
      }}}
      transition={{ duration: 0.5 }}
    >
      <motion.p
        className="mt-16 sacramento"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {t('summary.title')}
      </motion.p>

      <div className="my-12 px-4 w-full max-w-[450px] flex-1 flex flex-col justify-center">
        <div className="relative border rounded-full aspect-square flex flex-col justify-center items-center">
          <div className="animate-[spin_3s_linear_infinite] w-full h-full border border-[rgba(255,255,255,0.3)] rounded-full absolute top-[10px] left-[10px]" style={{ transformOrigin: '49% 50%' }} />
          <div className="animate-[spin_3s_linear_infinite] w-full h-full border border-[rgba(255,255,255,0.3)] rounded-full absolute top-[4px] -left-[12px]" style={{ transformOrigin: '50% 49%' }} />

          <AnimatePresence mode="wait">
            <motion.div
              key={`${step}-${loading ? 'loading' : 'ready'}`}
              className="p-6 flex flex-col items-center"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {loading && <p className="opacity-75">Loading...</p>}
              {!loading && summaryData && (
                <>
                  {step === 0 && (
                    <>
                      <p className="text-lg">
                        {t('summary.step1.content')}
                      </p>
                      <p className="text-2xl">
                        {t(meditationMinutes === 1 ? 'summary.step1.content2_one' : 'summary.step1.content2_multiple', { minutes: meditationMinutes })}
                      </p>
                    </>
                  )}

                  {step === 1 && summaryData.otherUsersCount > 0 && (
                    <>
                      <p>{summaryData.otherUsersCount === 1 ? t('summary.step2.content_one') : t('summary.step2.content_multiple')}</p>
                      <p>
                        <span className="text-2xl">
                          {t(summaryData.otherUsersCount === 1 ? 'summary.step2.content2_one' : 'summary.step2.content2_multiple', { otherUsersCount: summaryData.otherUsersCount })}
                        </span>{' '}
                      </p>
                      <p>{t('summary.step2.content3')}</p>
                    </>
                  )}
                  {step === 1 && summaryData.otherUsersCount === 0 && (
                    <div className="p-4">
                      <p>{t('summary.step2.contentAlone')}</p>
                    </div>
                  )}

                  {step === 2 && (
                    <>
                      <p className="mb-2">{t('summary.step3.content')}</p>
                      <p className="text-2xl">{t('summary.step3.content2')}</p>
                    </>
                  )}
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="min-h-[64px]">
        {!loading && (
          <motion.button
            className="btn primary mt-2 mb-4"
            onClick={() => { step === 2 ? goToPage("home") : setStep(step + 1) }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {step === 2 ? t('summary.home') : t('summary.next')}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default Summary;
